import { Component, Injector, AfterViewInit, OnDestroy } from '@angular/core';
import { AppComponentBase } from '@shared/common/app-component-base';
import { timer, fromEvent, Subject, Subscription } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { LocalStorageService } from '@shared/utils/local-storage.service';
import { AppConsts } from '@shared/AppConsts';
import { AppAuthService } from '../auth/app-auth.service';

@Component({
  standalone: false,
  selector: 'session-timeout',
  template: ''
})
export class SessionTimeoutComponent extends AppComponentBase implements AfterViewInit, OnDestroy {

  destroy$ = new Subject();

  private timeOutSecond = parseInt(this.s('App.UserManagement.SessionTimeOut.TimeOutSecond'));
  private lastActivityTimeStorageKey = 'Abp.SessionTimeOut.UserLastActivityTime';

  private IsUserActive = true;
  private notifierIsOpened = false;
  private subscriptions: Subscription[] = [];

  constructor(
    injector: Injector,
    private _localStorageService: LocalStorageService
  ) {
    super(injector);
  }

  ngAfterViewInit() {
    this.bindActions();
    this.writeToStorage(); // initialize store
    this.subscriptions.push(timer(1000, 1000).subscribe(() => {
      this.control();
    }));
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  private bindActions(): void {
    this.subscriptions.push(fromEvent(window, 'mousemove')
      .pipe(takeUntil(this.destroy$), debounceTime(350))
      .subscribe(() => {
        this.setUserActivity();
      }));

    this.subscriptions.push(fromEvent(window, 'mousedown')
      .pipe(takeUntil(this.destroy$), debounceTime(350))
      .subscribe(() => {
        this.setUserActivity();
      }));

    this.subscriptions.push(fromEvent(window, 'click')
      .pipe(takeUntil(this.destroy$), debounceTime(350))
      .subscribe(() => {
        this.setUserActivity();
      }));

    this.subscriptions.push(fromEvent(window, 'scroll')
      .pipe(takeUntil(this.destroy$), debounceTime(350))
      .subscribe(() => {
        this.setUserActivity();
      }));

    this.subscriptions.push(fromEvent(window, 'keypress')
      .pipe(takeUntil(this.destroy$), debounceTime(350))
      .subscribe(() => {
        this.setUserActivity();
      }));
  }

  private setUserActivity(): void {
    if (!this.notifierIsOpened) {
      this.IsUserActive = true;
    }
  }

  private control(): void {
    this.writeToStorage();
    this.controlStorage();
  }

  private writeToStorage(): void {
    if (this.IsUserActive) {
      if (localStorage) {
        this._localStorageService.setItem(this.lastActivityTimeStorageKey, Date.now().toString());
      } else {
        abp.utils.setCookieValue(this.lastActivityTimeStorageKey, Date.now().toString());
      }
    }
    this.IsUserActive = false;
  }

  private controlStorage(): void {
    if (localStorage) {
      this._localStorageService.getItem(this.lastActivityTimeStorageKey, (err, data) => {
        if (data) {
          let lastActivityTime = parseInt(data);
          this.controlValue(lastActivityTime);
        }
      });
    } else {
      let lastActivityTime = parseInt(abp.utils.getCookieValue(this.lastActivityTimeStorageKey));
      this.controlValue(lastActivityTime);
    }
  }
  private controlValue(lastActivityTime) {
    if (Date.now() - lastActivityTime <= this.timeOutSecond * 1000) {
      return;
    }

    if (!this.notifierIsOpened) {
      this.notifierIsOpened = true;
      this.redirectToLockScreen();
    }
  }

  private redirectToLockScreen(): void {
    const user = this.appSession.user;
    const tenant = this.appSession.tenant;

    if (user) {
      abp.utils.setCookieValue('userInfo', JSON.stringify(
        {
          userName: user.userName,
          emailAddress: user.emailAddress,
          profilePictureId: user.profilePictureId,
          tenant: tenant ? tenant.tenancyName : 'Host'
        }), undefined, abp.appPath);
      AppAuthService.markMicrosoftSsoResume(user.emailAddress || user.userName);
    }

    // Clear auth tokens client-side immediately — skip the LogOut API round-trip
    // so the redirect to session-locked is instant with no loading delay.
    // Keep the Microsoft/MSAL cache so "Sign in with Microsoft" can SSO silently.
    abp.auth.clearToken();
    abp.auth.clearRefreshToken();
    this._localStorageService.removeItem(AppConsts.authorization.encrptedAuthTokenName);
    location.href = '/account/session-locked';
  }
}
